/**
 * "Key Takeaways" derivation for blog articles.
 *
 * The box is the first thing a reader meets, above the fold, so a bad bullet
 * costs more trust than the whole box earns. The previous render-time version
 * took the first sentence of every H2 section and, when a section had no
 * prose, fell back to `section.slice(0, 120)` of tag-stripped text. On the
 * ranked-list and FAQ-heavy articles that produced bullets made of three
 * concatenated link titles.
 *
 * The rules here are therefore deliberately strict:
 *
 *  - Boilerplate sections (FAQ, Related Reading, conclusions) never contribute
 *    a bullet. They are navigation and sign-off, not findings, and between
 *    them they account for the majority of H2s in the corpus.
 *  - A section contributes only a complete sentence taken from a paragraph.
 *    There is no raw-text fallback: a section that cannot offer one is skipped.
 *  - Below MIN_TAKEAWAYS surviving bullets the whole box is dropped. A
 *    two-bullet summary of a 3,000-word article is worse than no summary.
 *
 * Pure and DOM-free so it can be unit-tested and, later, run at publish time
 * in the content pipeline instead of on every render.
 */

import { isRankedList } from "./rankedList";
import { findingAnchorId } from "@shared/citability";

/** Fewer surviving bullets than this and the box is not worth showing. */
export const MIN_TAKEAWAYS = 3;

/** Most bullets to show — past this the box stops being a summary. */
export const MAX_TAKEAWAYS = 7;

/**
 * Headings whose sections are navigation, Q&A or sign-off rather than
 * findings. Matched on the heading text, case-insensitively.
 */
const BOILERPLATE_HEADING =
  /^\s*(faq|faqs|frequently asked questions|common questions|questions|related reading|related articles|related posts|further reading|read next|references|sources|sources and references|the bottom line|bottom line|summing up|in summary|summary|conclusion|final thoughts|wrapping up|key takeaways|takeaways|what we covered)\s*[:.]?\s*$/i;

/** A complete sentence: starts capitalized, runs 25–300 chars, ends in terminal punctuation. */
const SENTENCE = /[A-Z][^.!?]{24,299}[.!?]/;

const H2_GLOBAL = /<h2[^>]*>([\s\S]*?)<\/h2>/gi;
const PARAGRAPH_GLOBAL = /<p[^>]*>([\s\S]*?)<\/p>/gi;

function stripTags(html: string): string {
  return html
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** Heading labels in document order, tags stripped. */
export function extractH2Labels(html: string): string[] {
  return Array.from(html.matchAll(H2_GLOBAL)).map((m) => stripTags(m[1]));
}

export function isBoilerplateHeading(label: string): boolean {
  return BOILERPLATE_HEADING.test(label);
}

/**
 * First complete sentence of a section's prose, or "" when the section has
 * none. Paragraphs are searched one at a time: matching across a whole section
 * lets the regex run through a <ul> or <table> and return a "sentence" made of
 * several list items with no punctuation between them.
 */
export function firstSentence(sectionHtml: string): string {
  for (const match of sectionHtml.matchAll(PARAGRAPH_GLOBAL)) {
    const text = stripTags(match[1]);
    const sentence = text.match(SENTENCE);
    if (sentence) return sentence[0].trim();
  }
  return "";
}

/**
 * Derive takeaway bullets from article HTML.
 *
 * Returns [] — meaning "render no box" — for ranked lists (one section per
 * provider yields a list of positioning lines above a list that is already
 * scannable) and whenever fewer than MIN_TAKEAWAYS sections offer a real
 * sentence.
 */
export function deriveKeyTakeaways(html: string): string[] {
  const labels = extractH2Labels(html);
  if (labels.length === 0) return [];
  if (isRankedList(labels)) return [];

  // Sections are the text between H2s; [0] is the intro, before the first H2.
  const sections = html.split(/<h2[^>]*>[\s\S]*?<\/h2>/i).slice(1);

  const bullets: string[] = [];
  sections.forEach((section, i) => {
    if (bullets.length >= MAX_TAKEAWAYS) return;
    const label = labels[i];
    if (!label || isBoilerplateHeading(label)) return;
    const sentence = firstSentence(section);
    if (sentence) bullets.push(sentence);
  });

  return bullets.length >= MIN_TAKEAWAYS ? bullets : [];
}

/**
 * Render bullets as the takeaway box, or "" for an empty list.
 *
 * Each bullet carries `id="finding-N"`, one-based, so a writer can link the
 * single sentence they are quoting instead of the page. That is the difference
 * between "Indexed found that…" with a link to our homepage and a citation that
 * lands on the number.
 */
export function renderKeyTakeawayBox(takeaways: string[]): string {
  if (takeaways.length === 0) return "";
  const items = takeaways
    .map((t, i) => `<li id="${findingAnchorId(i)}">${t}</li>`)
    .join("");
  return `<div class="key-takeaway auto-takeaway"><h4>Key Takeaways</h4><ul>${items}</ul></div>`;
}

/** True when the article already ships a hand-written takeaway box. */
export function hasAuthoredTakeaway(html: string): boolean {
  return html.includes('class="key-takeaway"');
}

/**
 * The takeaway box for a post: an authored box in the content wins, then
 * takeaways stored on the post by the publishing pipeline, then derivation
 * from the article body. Returns "" when none of the three produces a box.
 */
export function keyTakeawayBoxFor(post: { content: string; keyTakeaways?: string[] }): string {
  if (hasAuthoredTakeaway(post.content)) return "";
  const stored = (post.keyTakeaways ?? []).map((t) => t.trim()).filter(Boolean);
  if (stored.length >= MIN_TAKEAWAYS) return renderKeyTakeawayBox(stored.slice(0, MAX_TAKEAWAYS));
  return renderKeyTakeawayBox(deriveKeyTakeaways(post.content));
}
